import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";

interface CircularProgressRingProps {
    activeSegments: number;
    numberOfSegments: number
}

const CircularProgressRing: React.FC<CircularProgressRingProps> = ({ activeSegments, numberOfSegments }) => {
    const greenSegmentRefs = useRef<(SVGCircleElement | null)[]>([]);
    const completeCircleRef = useRef<SVGCircleElement>(null)
    const tickOneRef = useRef<SVGPathElement>(null)
    const tickTwoRef = useRef<SVGPathElement>(null)
    const circleRadius = 12;
    const circleCentre = 16;
    const totalCircumference = 2 * Math.PI * circleRadius;
    const totalSegments = numberOfSegments;
    const segmentLength = totalCircumference / totalSegments;

    const segmentOffsets = Array.from({ length: totalSegments }, (_, segmentIndex) => (
        -(segmentLength * segmentIndex)
    ));

    useEffect(() => {
        greenSegmentRefs.current.forEach((greenSegmentElement, segmentIndex) => {
            if (!greenSegmentElement) return;

            if (segmentIndex < activeSegments) {
                const tl = gsap.timeline();

                tl.to(greenSegmentElement, {
                    attr: { "stroke-dasharray": `${segmentLength} ${totalCircumference}` },
                    duration: 0.8,
                    ease: "power2.out",
                });
                if (activeSegments === totalSegments) {
                    const completeCircle = completeCircleRef?.current
                    const tickOne = tickOneRef?.current
                    const tickTwo = tickTwoRef?.current
                    const tickOneLength = tickOne?.getTotalLength()
                    const tickTwoLength = tickTwo?.getTotalLength()
                        gsap.set(tickOne, {
                            attr: {
                                "stroke-dasharray": tickOneLength,
                                "stroke-dashoffset": tickOneLength
                            }
                        })
                        gsap.set(tickTwo, {
                            attr: {
                                "stroke-dasharray": tickTwoLength,
                                "stroke-dashoffset": tickTwoLength
                            }
                        })
                        tl.to(completeCircle, {
                            attr: { "r": circleRadius },
                            duration: 0.4,
                            ease: "power2.out"
                        }, '-=0.4')
                        tl.to(tickOne, {
                            attr: { 'stroke-dashoffset': 0 },
                            duration: 0.3,
                            ease: "power2.out"

                        })
                        tl.to(tickTwo, {
                            attr: { 'stroke-dashoffset': 0 },
                            duration: 0.3,
                            ease: "power2.out"

                        })
                    
                }
            } else {
                gsap.to(greenSegmentElement, {
                    attr: { "stroke-dasharray": `0 ${totalCircumference}` },
                    duration: 0.8,
                    ease: "power2.out",
                });
            }
        });
    }, [activeSegments]);

    return (
        <svg width="32" height="32" viewBox="0 0 32 32" style={{zIndex: 200} }>
            <g transform={`rotate(-90, ${circleCentre}, ${circleCentre})`}>
                {segmentOffsets.map((segmentOffset, segmentIndex) => (
                    <circle
                        key={segmentIndex}
                        cx={circleCentre}
                        cy={circleCentre}
                        r={circleRadius}
                        fill="none"
                        stroke="#1a1a1a"
                        strokeWidth={6}
                        strokeDasharray={`${segmentLength} ${totalCircumference}`}
                        strokeDashoffset={segmentOffset}
                    />
                ))}

                {segmentOffsets.map((segmentOffset, segmentIndex) => (
                    <circle
                        key={`green-${segmentIndex}`}
                        ref={greenSegmentElement => { greenSegmentRefs.current[segmentIndex] = greenSegmentElement; }}
                        cx={circleCentre}
                        cy={circleCentre}
                        r={circleRadius}
                        fill="none"
                        stroke="#00BF00"
                        strokeWidth={6}
                        strokeDasharray={`0 ${totalCircumference}`}
                        strokeDashoffset={segmentOffset}
                    />
                ))}
                {segmentOffsets.length === activeSegments &&
                    <g>
                        <circle
                            ref={completeCircleRef}
                            cx={circleCentre}
                            cy={circleCentre}
                            r={0}
                            fill="#00BF00"
                        />
                        <g transform={`rotate(90, ${circleCentre}, ${circleCentre})`}>
                        <path
                            ref={tickOneRef}
                            stroke="white"
                            strokeWidth="4"
                            strokeLinecap="round"
                            d="M8 17L14 23"
                        />
                        <path
                            ref={tickTwoRef}
                            stroke="white"
                            strokeWidth="4"
                            strokeLinecap="round"
                            d="M14 23L22 9"
                            />
                        </g>
                    </g>
                }
            </g>
        </svg>
    );
};

export default CircularProgressRing;